import React from 'react';

const UPCOMING = [
  'bg-pink-50 border-pink-200 text-pink-600',
  'bg-amber-50 border-amber-200 text-amber-600',
  'bg-sky-50 border-sky-200 text-sky-600',
  'bg-violet-50 border-violet-200 text-violet-600',
  'bg-teal-50 border-teal-200 text-teal-600',
];

interface LessonCharacterQueueProps {
  lessonText: string;
  typedText: string;
  windowBefore?: number;
  windowAfter?: number;
}

// A compact, sliding queue of upcoming characters -- shows a handful of
// completed characters (green, checked) sliding past a highlighted current
// character, followed by what's coming up next. This replaces displaying the
// entire lesson text as one block, which is friendlier for younger students.
const LessonCharacterQueue: React.FC<LessonCharacterQueueProps> = ({ lessonText, typedText, windowBefore = 2, windowAfter = 7 }) => {
  const currentIndex = typedText.length;
  const start = Math.max(0, currentIndex - windowBefore);
  const end = Math.min(lessonText.length, currentIndex + windowAfter + 1);
  const chars = lessonText.slice(start, end).split('');

  return (
    <div className="flex items-center justify-center gap-1.5 md:gap-2 py-4 overflow-hidden">
      {chars.map((char, i) => {
        const absoluteIndex = start + i;
        const isPast = absoluteIndex < currentIndex;
        const isCurrent = absoluteIndex === currentIndex;
        const wasCorrect = isPast && typedText[absoluteIndex] === char;
        const wasWrong = isPast && typedText[absoluteIndex] !== char;
        const isSpace = char === ' ';

        // Upcoming letters get a rainbow of pastel chips; green/red stay reserved for right/wrong.
        let chipClass = UPCOMING[absoluteIndex % UPCOMING.length];
        if (wasCorrect) chipClass = 'bg-green-100 border-green-400 text-green-700';
        if (wasWrong) chipClass = 'bg-red-100 border-red-400 text-red-600';
        if (isCurrent) chipClass = 'bg-gradient-to-br from-sky-400 to-violet-500 border-white text-white shadow-lg ring-4 ring-sky-200';

        return (
          <div
            key={absoluteIndex}
            className={`relative flex items-center justify-center w-9 h-9 md:w-12 md:h-12 rounded-xl border-2 font-mono text-base md:text-xl font-bold transition-colors duration-200 ${chipClass} ${isCurrent ? 'fun-bob z-10' : 'opacity-90'}`}
          >
            {wasCorrect && (
              <span className="absolute -top-2 -right-1.5 w-4 h-4 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center">&#10003;</span>
            )}
            {isCurrent && (
              <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-violet-400" />
            )}
            {isSpace ? <span className="w-4 h-0.5 bg-current opacity-30 rounded" /> : char}
          </div>
        );
      })}
    </div>
  );
};

export default LessonCharacterQueue;
